import { API_CONFIG, TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from '../config/api';
import SessionManager from '../utils/SessionManager';

/**
 * Centralized HTTP client for all API communications
 * Handles authentication, error handling, and request/response interceptors
 */
class ApiClient {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  /**
   * Get authentication token from SessionManager
   */
  getAuthToken() {
    return SessionManager.getToken();
  }

  /**
   * Create request headers with authentication and content type
   */
  createHeaders(options = {}) {
    const headers = {
      ...options.headers
    };

    // Only set Content-Type for non-FormData requests
    if (!options.isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const token = this.getAuthToken();
    
    if (token && !options.skipAuth) {
      headers['Authorization'] = `Bearer ${token}`;
    } else if (!token && !options.skipAuth) {
      console.warn('[ApiClient] No token available for authenticated request');
    }

    return headers;
  }

  /**
   * Create AbortController for request timeout
   */
  createAbortController() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    return { controller, timeoutId };
  }

  /**
   * Handle fetch response and parse JSON
   */
  async handleResponse(response) {
    // Handle 204 No Content (common for DELETE requests)
    if (response.status === 204) {
      return { success: true, message: 'Operation completed successfully' };
    }

    let data;
    const contentType = response.headers.get('content-type');
    const contentLength = response.headers.get('content-length');
    
    // For empty responses or no content type, return success for successful requests
    if ((contentLength === '0' || !contentType) && response.ok) {
      return { success: true, message: 'Operation completed successfully' };
    }
    
    // Read response body once
    let responseText = '';
    try {
      responseText = await response.text();
    } catch (textError) {
      // If reading text fails but response is OK, return success
      if (response.ok) {
        console.warn('[ApiClient] Failed to read response text, but request was successful:', textError);
        return { success: true, message: 'Operation completed successfully' };
      }
      // For error responses, create error data
      responseText = JSON.stringify({ message: 'Failed to read response' });
    }
    
    // Parse the response text
    if (contentType && contentType.includes('application/json')) {
      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        // If parsing fails but response is OK, return success
        if (response.ok) {
          console.warn('[ApiClient] Failed to parse JSON response, but request was successful:', parseError);
          return { success: true, message: 'Operation completed successfully' };
        }
        // For error responses, use the text as message
        data = { message: responseText || 'Failed to parse response' };
      }
    } else {
      // Non-JSON response
      data = responseText || (response.ok ? { success: true, message: 'Operation completed successfully' } : { message: 'Request failed' });
    }

    if (!response.ok) {
      // Handle 307 Temporary Redirect specifically
      if (response.status === 307) {
        console.log('[ApiClient] 307 Redirect detected, but fetch should have followed it automatically');
        const error = new Error('Redirect error - check API endpoint configuration');
        error.status = response.status;
        error.data = data;
        throw error;
      }
      
      const error = new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
      error.status = response.status;
      error.data = data;
      
      // Handle 401 Unauthorized - clear tokens and redirect to login
      if (response.status === 401) {
        console.log('[ApiClient] 401 Unauthorized - clearing tokens and forcing logout');
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(USER_STORAGE_KEY);
        
        // Clear any other session-related data
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('seemanchal_')) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        
        // Trigger global 401 handler if available
        if (typeof window !== 'undefined' && window.handle401Error) {
          window.handle401Error();
        }
      }
      
      throw error;
    }

    return data;
  }

  /**
   * Make HTTP request with error handling and interceptors
   */
  async makeRequest(url, options = {}) {
    const { controller, timeoutId } = this.createAbortController();
    
    try {
      const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;
      const headers = this.createHeaders(options);

      console.log('[ApiClient] Making request to:', fullUrl);
      console.log('[ApiClient] Request method:', options.method || 'GET');
      console.log('[ApiClient] Request headers:', headers);
      if (options.body) {
        console.log('[ApiClient] Request body:', typeof options.body === 'object' ? JSON.stringify(options.body, null, 2) : options.body);
      }

      const fetchOptions = {
        ...options,
        headers,
        signal: controller.signal,
        redirect: 'follow' // Explicitly follow redirects
      };

      // Handle FormData (for file uploads)
      if (options.isFormData && options.body instanceof FormData) {
        // Don't set Content-Type for FormData, browser will set it with boundary
        delete fetchOptions.headers['Content-Type'];
        fetchOptions.body = options.body;
      } else if (options.body && typeof options.body === 'object') {
        // Regular JSON body
        fetchOptions.body = JSON.stringify(options.body);
      }

      const response = await fetch(fullUrl, fetchOptions);
      clearTimeout(timeoutId);
      
      return await this.handleResponse(response);
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timeout');
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }
      
      // Handle network errors
      if (!error.status) {
        const networkError = new Error('Network error. Please check your connection.');
        networkError.name = 'NetworkError';
        throw networkError;
      }
      
      throw error;
    }
  }

  /**
   * GET request
   */
  async get(url, options = {}) {
    return this.makeRequest(url, {
      ...options,
      method: 'GET'
    });
  }

  /**
   * POST request
   */
  async post(url, data = null, options = {}) {
    return this.makeRequest(url, {
      ...options,
      method: 'POST',
      body: data
    });
  }

  /**
   * PUT request
   */
  async put(url, data = null, options = {}) {
    return this.makeRequest(url, {
      ...options,
      method: 'PUT',
      body: data
    });
  }

  /**
   * DELETE request
   */
  async delete(url, options = {}) {
    return this.makeRequest(url, {
      ...options,
      method: 'DELETE'
    });
  }

  /**
   * PATCH request
   */
  async patch(url, data = null, options = {}) {
    return this.makeRequest(url, {
      ...options,
      method: 'PATCH',
      body: data
    });
  }
}

// Export singleton instance
const apiClient = new ApiClient();
export default apiClient;